import type {
  SettlementState,
  SigningOperation,
  SigningOperationLinks,
  SignerRoleV2,
} from "../types/mobileSigning";

export type EgovPhase =
  | "idle"
  | "starting"
  | "launching"
  | "awaiting"
  | "finalizing"
  | "expired"
  | "done"
  | "canceled"
  | "failed"
  | "settlement_failed";

const TERMINAL_PHASES: EgovPhase[] = ["done", "canceled", "failed", "settlement_failed"];

export function isTerminalEgovPhase(phase: EgovPhase): boolean {
  return TERMINAL_PHASES.includes(phase);
}

/**
 * The server also stops sending a cadence once it considers an operation
 * locally settled (e.g. while finalizing), so `null` alone cannot be read as
 * "nothing left to poll" — callers that need to keep polling through such a
 * phase must supply their own interval instead of trusting this fallback.
 */
export function getEgovPollInterval(operation: SigningOperation | null): number {
  if (!operation) return 0;
  return operation.next_poll_ms ?? 0;
}

export function isEgovLaunchExpired(expireAt: number | null, now: number): boolean {
  if (!expireAt) return false;
  return expireAt <= now;
}

export function isSigningSettled(status: string | undefined, settlementState: SettlementState): boolean {
  return status === "done" && settlementState !== "pending";
}

export function deriveEgovPhase(
  operation: SigningOperation | null,
  isStarting: boolean,
  isExpired = false,
  isAwaitingLaunch = false,
): EgovPhase {
  if (isStarting) return "starting";
  // The provider outcome was unknown, so there is nothing to scan yet and
  // reconciliation is still working on it.
  if (isAwaitingLaunch) return "launching";
  if (!operation) return "idle";

  if (operation.status === "canceled" || operation.status === "canceledFromEgov") return "canceled";
  if (operation.status === "fail") return "failed";
  if (operation.operation_state === "canceled") return "canceled";
  if (operation.operation_state === "failed") return "failed";

  const isSignatureAccepted =
    operation.status === "done"
    || operation.operation_state === "verifying"
    || operation.operation_state === "completed";

  if (isSignatureAccepted) {
    const settlement = operation.settlement?.state ?? "settled";
    if (settlement === "failed") return "settlement_failed";
    return settlement === "settled" ? "done" : "finalizing";
  }

  if (isExpired) return "expired";
  return "awaiting";
}

export function getEgovPhaseKey(phase: EgovPhase): string {
  return `signing.egov.phases.${phase}`;
}

/**
 * The organization signs with a business certificate (IIN + BIN), which lives
 * in eGov Business; personal signers use eGov mobile. The backend returns both
 * links without picking one.
 */
export function pickEgovLink(
  links: Pick<SigningOperationLinks, "egov_mobile_link" | "egov_business_link">,
  signerRole?: SignerRoleV2,
): { primary: string; secondary: string; isBusiness: boolean } {
  const isBusiness = signerRole === "organization" || signerRole === "org";
  return {
    primary: isBusiness ? links.egov_business_link : links.egov_mobile_link,
    secondary: isBusiness ? links.egov_mobile_link : links.egov_business_link,
    isBusiness,
  };
}

export function formatRemaining(expireAt: number, now: number): string {
  const totalSeconds = Math.max(0, Math.floor((expireAt - now) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const tail = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  if (hours > 0) return `${hours}:${tail}`;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
